
import React from 'react';
import { ShoppingCart, Layout, Sparkles, User, Search, Flower, Globe, LogOut, ClipboardList, Palette, MessageCircle, Settings } from 'lucide-react';
import { Language, User as UserType } from '../types';

interface NavbarProps {
  cartCount: number;
  onCartClick: () => void;
  onChatClick: () => void;
  onAdminClick: () => void;
  onNavigate: (page: string) => void;
  onUserAuthClick: () => void;
  currentUser: UserType | null;
  onLogout: () => void;
  activePage: string;
  language: Language;
  onToggleLanguage: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ 
  cartCount, 
  onCartClick, 
  onChatClick,
  onAdminClick,
  onNavigate, 
  onUserAuthClick,
  currentUser,
  onLogout,
  activePage, 
  language, 
  onToggleLanguage 
}) => {
  const t = {
    pt: { home: 'Início', gallery: 'Galeria', custom: 'Arte Custom', elite: 'Conta', logout: 'Sair', myOrders: 'Meus Pedidos', chat: 'Chat' },
    en: { home: 'Home', gallery: 'Gallery', custom: 'Custom Art', elite: 'Account', logout: 'Logout', myOrders: 'My Orders', chat: 'Chat' }
  }[language];

  return (
    <nav className="sticky top-0 z-50 bg-[#08090a] border-b border-white/5 px-4 md:px-8 py-3 md:py-4 overflow-visible">
      <div className="max-w-[1600px] mx-auto flex items-center justify-between overflow-visible">
        <div 
          className="flex items-center gap-3 cursor-pointer group whitespace-nowrap overflow-visible flex-shrink-0" 
          onClick={() => onNavigate('home')}
        >
          <div className="bg-white p-2 group-hover:bg-[#ff9eb5] transition-all shadow-[0_0_15px_rgba(255,158,181,0.3)] group-hover:shadow-[0_0_25px_rgba(255,158,181,0.6)] overflow-visible flex-shrink-0 flex items-center justify-center">
            <Flower className="w-4 h-4 text-black group-hover:text-white transition-transform group-hover:rotate-12" />
          </div>
          <span className="text-base md:text-lg font-black tracking-tighter text-white uppercase font-sans hidden sm:block pr-6 overflow-visible">
            NIIKO<span className="text-[#ff9eb5] opacity-90 ml-1"> ARTWORK</span>
          </span>
        </div>

        <div className="hidden lg:flex items-center gap-10">
          {[
            { id: 'home', label: t.home, icon: Layout },
            { id: 'gallery', label: t.gallery, icon: Search },
            { id: 'custom-art', label: t.custom, icon: Palette },
          ].map((link) => (
            <button
              key={link.id}
              onClick={() => onNavigate(link.id)}
              className={`flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.4em] transition-all hover:text-[#ff9eb5] ${
                activePage === link.id ? 'text-[#ff9eb5]' : 'text-zinc-500'
              }`}
            >
              <link.icon className="w-4 h-4" />
              {link.label}
            </button>
          ))}
          
          {currentUser && (
            <button
              onClick={() => onNavigate('my-orders')}
              className={`flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.4em] transition-all hover:text-[#ff9eb5] ${
                activePage === 'my-orders' ? 'text-[#ff9eb5]' : 'text-zinc-500'
              }`}
            >
              <ClipboardList className="w-4 h-4" />
              {t.myOrders}
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 md:gap-5">
          <div className="flex items-center bg-black/40 border border-zinc-800 p-1">
            <button 
              onClick={() => language !== 'pt' && onToggleLanguage()}
              className={`px-3 py-1.5 transition-all text-[9px] font-black ${language === 'pt' ? 'bg-zinc-800 text-[#ff9eb5]' : 'text-zinc-600'}`}
            >
              BRL
            </button>
            <button 
              onClick={() => language !== 'en' && onToggleLanguage()}
              className={`px-3 py-1.5 transition-all text-[9px] font-black ${language === 'en' ? 'bg-zinc-800 text-[#ff9eb5]' : 'text-zinc-600'}`}
            >
              USD
            </button>
          </div>

          <button 
            onClick={onAdminClick}
            className="p-2.5 bg-zinc-950 border border-zinc-800 text-zinc-500 hover:text-[#ff9eb5] transition-all"
            title="Painel Admin"
          >
            <Settings className="w-5 h-5" />
          </button>

          <button 
            onClick={onChatClick}
            className="p-2.5 bg-zinc-950 border border-zinc-800 text-zinc-500 hover:text-[#ff9eb5] transition-all"
          >
            <MessageCircle className="w-5 h-5" />
          </button>

          <button 
            onClick={onCartClick}
            className="relative p-2.5 bg-zinc-950 border border-zinc-800 text-zinc-500 hover:text-[#ff9eb5] transition-all"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#ff9eb5] text-white text-[8px] font-black px-1.5 py-0.5">
                {cartCount}
              </span>
            )}
          </button>
          
          {currentUser ? (
            <button 
              onClick={() => onNavigate('profile')}
              className={`flex items-center gap-3 px-4 py-2 border transition-all ${
                activePage === 'profile' ? 'bg-[#ff9eb5] border-[#ff9eb5]' : 'bg-white/5 border-white/5 hover:bg-white/10'
              }`}
            >
              <div className="w-6 h-6 rounded-full overflow-hidden bg-black border border-white/10 flex-shrink-0">
                {currentUser.avatarUrl ? (
                  <img src={currentUser.avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-3 h-3 text-zinc-500" />
                  </div>
                )}
              </div>
              <span className="text-[8px] font-black text-white uppercase tracking-tight truncate max-w-[80px]">{currentUser.name}</span>
            </button>
          ) : (
            <button 
              onClick={onUserAuthClick}
              className="hidden sm:flex items-center gap-3 bg-white text-black hover:bg-[#ff9eb5] hover:text-white px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] transition-all"
            >
              <User className="w-4 h-4" /> {t.elite}
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
